import React from "react";
import { Link } from "react-router-dom";
import { useSEO } from "../lib/seo";

const STEPS = [
  ["01","Choose your product","Pick a T-shirt, oversized tee, hoodie or jersey."],
  ["02","Select or send your design","Browse our design library or send us your artwork."],
  ["03","Confirm size, quantity and price","Choose your size and quantity. Ready-to-buy products continue through checkout; custom orders are confirmed by WhatsApp."],
  ["04","Production and delivery","We print your order and ship it to your address."],
];

const REQUIREMENTS = [
  ["Product","Tell us what you want: T-shirt, oversized tee, hoodie, jersey or another custom option."],
  ["Size & quantity","Choose the size and quantity for your order."],
  ["Artwork or design","Use a design from our library, send your artwork, or ask for design assistance."],
  ["Personalisation","Share names, numbers, text or other details you want printed."],
];

export default function HowItWorks() {
  useSEO({
    title: "How It Works — PrintMyTees",
    description: "See how PrintMyTees works: choose a product, select or send a design, confirm your order, and get it delivered.",
  });

  return (
    <div className="container-pmt py-10 sm:py-12" data-testid="how-page">
      <div className="overline">How It Works</div>
      <h1 className="display-h2 mt-2 max-w-3xl">Simple, transparent process.</h1>
      <p className="mt-4 max-w-2xl text-sm sm:text-base text-stone-600">
        Choose a product, pick a design or share your artwork, confirm the details, and we take care of production and delivery.
      </p>

      <div className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {STEPS.map(([n,t,d], index) => (
          <div key={n} className="card-pmt p-5 sm:p-6 relative" data-testid={`step-${n}`}>
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-10 h-10 border border-[#D95328] flex items-center justify-center font-mono text-xs text-[#D95328]" aria-hidden="true">
                {n}
              </div>
              <div>
                <div className="font-bold uppercase text-xl sm:text-2xl leading-tight" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{t}</div>
                <div className="mt-2 text-sm leading-6 text-stone-600">{d}</div>
              </div>
            </div>
            {index < STEPS.length - 1 && (
              <div className="hidden md:block absolute -right-3 top-1/2 w-6 border-t border-dashed border-[#E6DFD5]" aria-hidden="true" />
            )}
          </div>
        ))}
      </div>

      <div className="mt-12 sm:mt-16">
        <div className="overline">What You Need</div>
        <h2 className="display-h2 mt-2 max-w-3xl">A few details help us get your order right.</h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {REQUIREMENTS.map(([title, text]) => (
            <div key={title} className="border border-[#E6DFD5] bg-white p-4 sm:p-5">
              <div className="font-semibold text-stone-900">{title}</div>
              <div className="mt-1 text-sm leading-6 text-stone-600">{text}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12 sm:mt-16">
        <div className="overline">Why PrintMyTees</div>
        <h2 className="display-h2 mt-2 max-w-3xl">A real Indian custom apparel studio.</h2>
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <li className="border border-[#E6DFD5] bg-white p-4">Custom designs — your idea, your artwork</li>
          <li className="border border-[#E6DFD5] bg-white p-4">Multiple printing methods: DTF, Vinyl, Sublimation</li>
          <li className="border border-[#E6DFD5] bg-white p-4">Individual and bulk orders</li>
          <li className="border border-[#E6DFD5] bg-white p-4">Personalised apparel for couples, teams and events</li>
          <li className="border border-[#E6DFD5] bg-white p-4">WhatsApp support end to end</li>
          <li className="border border-[#E6DFD5] bg-white p-4">Design assistance included</li>
        </ul>
      </div>

      <div className="mt-10 sm:mt-12 border border-[#E6DFD5] bg-[#F3EDE2] p-5 sm:p-6">
        <div className="overline">Choose Your Path</div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-bold uppercase" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
          Ready-made design or your own idea?
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
          Browse our products and designs for a straightforward purchase, or send us your requirements for a custom quote.
        </p>
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <Link to="/shop" className="btn-primary w-full sm:w-auto text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2" data-testid="how-shop-btn">
            Shop Products
          </Link>
          <Link to="/designs" className="btn-outline-pmt w-full sm:w-auto text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2" data-testid="how-designs-btn">
            Browse Designs
          </Link>
          <Link to="/custom-orders" className="btn-outline-pmt w-full sm:w-auto text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2" data-testid="how-custom-btn">
            Start a Custom Order
          </Link>
        </div>
      </div>
    </div>
  );
}
